import { useCallback, useEffect, useRef, useState } from "react"
import { Platform } from "react-native"

const INACTIVITY_TIMEOUT_MS = 3000

/**
 * Tracks mouse activity on web for YouTube-style player controls:
 * controls appear on mouse movement and fade after 3 seconds of inactivity.
 *
 * On native, isMouseActive is always false so callers can safely include it
 * in expressions like `!isPlaying || (Platform.OS === "web" && isMouseActive)`
 * without changing native behavior.
 */
export function useWebMouseActivity() {
  const [isMouseActive, setIsMouseActive] = useState(true)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleActivity = useCallback(() => {
    if (Platform.OS !== "web") return
    setIsMouseActive(true)
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      setIsMouseActive(false)
    }, INACTIVITY_TIMEOUT_MS)
  }, [])

  useEffect(() => {
    if (Platform.OS !== "web") return
    document.addEventListener("keydown", handleActivity)
    // Prime the timer so controls eventually fade if the user is idle
    handleActivity()
    return () => {
      document.removeEventListener("keydown", handleActivity)
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [handleActivity])

  return {
    isMouseActive: Platform.OS === "web" ? isMouseActive : false,
    handleMouseMove: handleActivity,
  }
}
